import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  ArrowLeft, Save, Plus, Trash2, Upload, Image, Film, FileText, DollarSign,
  Users, Target, MapPin, Calendar, Briefcase, Globe, X, Link2, BookOpen,
  UsersRound, ClipboardCheck, ExternalLink, ArrowRight, CheckCircle, Clock, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import InvestmentSelfAssessment from './InvestmentSelfAssessment';

const API = BACKEND_URL;

const SDG_LABELS = {
  1: 'No Poverty', 2: 'Zero Hunger', 3: 'Good Health', 4: 'Quality Education',
  5: 'Gender Equality', 6: 'Clean Water', 7: 'Affordable Energy', 8: 'Decent Work',
  9: 'Industry & Innovation', 10: 'Reduced Inequalities', 11: 'Sustainable Cities',
  12: 'Responsible Consumption', 13: 'Climate Action', 14: 'Life Below Water',
  15: 'Life on Land', 16: 'Peace & Justice', 17: 'Partnerships',
};

const INSTRUMENT_OPTIONS = [
  { value: 'direct_grant', label: 'Grant / Subsidy' },
  { value: 'social_bond', label: 'Social Bond' },
  { value: 'equity', label: 'Equity' },
  { value: 'loan', label: 'Loan' },
  { value: 'guarantee', label: 'Guarantee' },
  { value: 'other', label: 'Other' },
];

const TYPE_OPTIONS = [
  { value: 'grant', label: 'Grant' },
  { value: 'bond', label: 'Bond' },
  { value: 'equity', label: 'Equity' },
  { value: 'loan', label: 'Loan' },
  { value: 'blended', label: 'Blended' },
];

const InvestmentDetailView = ({ investment, onBack, onUpdated, user }) => {
  const navigate = useNavigate();
  const [inv, setInv] = useState({ ...investment });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [linkedEvaluations, setLinkedEvaluations] = useState([]);
  const [accessInfo, setAccessInfo] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);
  const [newFeedback, setNewFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('recommendation');
  const [allUsers, setAllUsers] = useState([]);
  const [grantUserId, setGrantUserId] = useState('');
  const [grantPermission, setGrantPermission] = useState('view');
  const [socialActors, setSocialActors] = useState([]);
  const photoInputRef = useRef(null);
  const attachInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLinkedEvaluations();
    fetchAccessInfo();
    fetchFeedback();
  }, [inv.id]);

  useEffect(() => {
    // Load registered Social Actors once for the owner-organisation picker.
    (async () => {
      try {
        const res = await axios.get(`${API}/api/social-actors`, { headers });
        const list = Array.isArray(res.data) ? res.data : (res.data.actors || []);
        setSocialActors(list);
      } catch {
        setSocialActors([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canEdit = accessInfo?.current_user_permission === 'edit';

  const fetchAccessInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/social-investments/${inv.id}/access`, { headers });
      setAccessInfo(res.data);
    } catch { /* ignore */ }
  };

  const fetchFeedback = async () => {
    try {
      const res = await axios.get(`${API}/api/social-investments/${inv.id}/feedback`, { headers });
      setFeedbackList(res.data.feedback || []);
    } catch { /* ignore */ }
  };

  const fetchAllUsers = async () => {
    if (allUsers.length > 0) return;
    try {
      const res = await axios.get(`${API}/api/admin/users`, { headers });
      setAllUsers((res.data || []).filter(u => u.id !== inv.creator_id));
    } catch { setAllUsers([]); }
  };

  const handleGrantAccess = async () => {
    if (!grantUserId) return;
    try {
      const res = await axios.post(`${API}/api/social-investments/${inv.id}/access`, { user_id: grantUserId, permission: grantPermission }, { headers });
      setAccessInfo(prev => ({ ...prev, grants: res.data.grants }));
      setGrantUserId('');
      toast.success('Access granted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userId) => {
    try {
      await axios.delete(`${API}/api/social-investments/${inv.id}/access/${userId}`, { headers });
      setAccessInfo(prev => ({ ...prev, grants: (prev.grants || []).filter(g => g.user_id !== userId) }));
      toast.success('Access revoked');
    } catch {
      toast.error('Failed to revoke');
    }
  };

  const handleSubmitFeedback = async () => {
    if (!newFeedback.trim()) return;
    try {
      const res = await axios.post(`${API}/api/social-investments/${inv.id}/feedback`, { content: newFeedback, feedback_type: feedbackType }, { headers });
      setFeedbackList(prev => [res.data, ...prev]);
      setNewFeedback('');
      toast.success('Feedback submitted');
    } catch {
      toast.error('Failed to submit feedback');
    }
  };

  const handleDeleteFeedback = async (fbId) => {
    try {
      await axios.delete(`${API}/api/social-investments/${inv.id}/feedback/${fbId}`, { headers });
      setFeedbackList(prev => prev.filter(f => f.id !== fbId));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const fetchLinkedEvaluations = async () => {
    try {
      const res = await axios.get(`${API}/api/investment-evaluation/evaluations?investment_id=${inv.id}`, { headers });
      setLinkedEvaluations(res.data.evaluations || []);
    } catch {
      setLinkedEvaluations([]);
    }
  };

  const convertToBlended = async () => {
    if (!window.confirm(
      'Promote this investment to the Combined Investments module?\n\n' +
      'This will:\n' +
      '• Create a new draft Blended Plan pre-filled from this investment\n' +
      '• Convert each funding source into a tranche/segment\n' +
      '• Allow you to invite social investors to fund the plan\n\n' +
      'You can still edit everything afterwards in the Combined Investments module.'
    )) return;
    try {
      const resp = await axios.post(
        `${API}/api/social-investments/${inv.id}/convert-to-blended`, {}, { headers }
      );
      toast.success(`Blended Plan created — ${resp.data.segments_created} segments mapped.`);
      // Refresh the investment so the linked-bi banner shows up
      const refreshed = await axios.get(`${API}/api/social-investments/${inv.id}`, { headers });
      setInv(refreshed.data);
      if (onUpdated) onUpdated(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Promotion failed');
    }
  };

  const handleSave = async () => {
    if (!inv.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: inv.title,
        description: inv.description || '',
        investment_type: inv.investment_type || 'grant',
        financial_instrument: inv.financial_instrument || 'direct_grant',
        sector: inv.sector || '',
        beneficiaries: inv.beneficiaries || [],
        estimated_budget: inv.estimated_budget || 0,
        currency: inv.currency || 'EUR',
        target_beneficiary_count: inv.target_beneficiary_count || 0,
        location: inv.location || '',
        tags: inv.tags || [],
        participatory_design_enabled: inv.participatory_design_enabled || false,
        target_groups: inv.target_groups || [],
        expected_outcomes: inv.expected_outcomes || [],
        sdg_alignment: inv.sdg_alignment || [],
        budget_breakdown: inv.budget_breakdown || [],
        funding_sources: inv.funding_sources || [],
        timeline_months: inv.timeline_months || 0,
        start_date: inv.start_date || '',
        stakeholders: inv.stakeholders || [],
        video_urls: inv.video_urls || [],
        social_actor_id: inv.social_actor_id || null,
      };
      const res = await axios.put(`${API}/api/social-investments/${inv.id}`, payload, { headers });
      setInv(res.data);
      if (res.data?._pending) {
        toast.success(
          res.data._pending_message ||
          'Your changes were submitted and are pending review by an Administrator or Project Manager. The investment will not be updated until approved.',
          { duration: 6000 }
        );
      } else {
        toast.success('Investment saved');
      }
      if (onUpdated) onUpdated(res.data);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only images allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/api/social-investments/${inv.id}/photos`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo uploaded');
      const res = await axios.get(`${API}/api/social-investments/${inv.id}`, { headers });
      setInv(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/api/social-investments/${inv.id}/photos/${photoId}`, { headers });
      setInv(prev => ({ ...prev, photos: (prev.photos || []).filter(p => p.id !== photoId) }));
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  // Attachment upload
  const handleAttachUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/api/social-investments/${inv.id}/attachments`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded');
      const res = await axios.get(`${API}/api/social-investments/${inv.id}`, { headers });
      setInv(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (attachInputRef.current) attachInputRef.current.value = '';
    }
  };

  const handleDeleteAttach = async (attachId) => {
    try {
      await axios.delete(`${API}/api/social-investments/${inv.id}/attachments/${attachId}`, { headers });
      setInv(prev => ({ ...prev, attachments: (prev.attachments || []).filter(a => a.id !== attachId) }));
      toast.success('File removed');
    } catch {
      toast.error('Failed to remove file');
    }
  };

  // Video URLs
  const handleAddVideo = async () => {
    if (!videoUrlInput.trim()) return;
    try {
      await axios.post(`${API}/api/social-investments/${inv.id}/video-urls`, { url: videoUrlInput.trim() }, { headers });
      setInv(prev => ({ ...prev, video_urls: [...(prev.video_urls || []), videoUrlInput.trim()] }));
      setVideoUrlInput('');
      toast.success('Video link added');
    } catch {
      toast.error('Failed to add video');
    }
  };

  const handleRemoveVideo = async (url) => {
    try {
      await axios.delete(`${API}/api/social-investments/${inv.id}/video-urls`, { headers, data: { url } });
      setInv(prev => ({ ...prev, video_urls: (prev.video_urls || []).filter(u => u !== url) }));
    } catch {
      toast.error('Failed to remove video');
    }
  };

  // Helpers for dynamic lists
  const addToList = (field, template) => setInv(prev => ({ ...prev, [field]: [...(prev[field] || []), template] }));
  const updateListItem = (field, idx, key, value) => {
    setInv(prev => {
      const list = [...(prev[field] || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...prev, [field]: list };
    });
  };
  const removeFromList = (field, idx) => setInv(prev => ({ ...prev, [field]: (prev[field] || []).filter((_, i) => i !== idx) }));

  const toggleSDG = (sdg) => {
    setInv(prev => {
      const current = prev.sdg_alignment || [];
      return { ...prev, sdg_alignment: current.includes(sdg) ? current.filter(s => s !== sdg) : [...current, sdg].sort((a, b) => a - b) };
    });
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getYouTubeId = (url) => {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?#]+)/);
    return match ? match[1] : null;
  };

  return (
    <div className="space-y-4 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="back-to-list-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{inv.title || 'New Investment'}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className="capitalize text-xs">{inv.status}</Badge>
              {inv.self_assessment?.maturity_level && (
                <Badge className="text-xs" style={{ backgroundColor: inv.self_assessment.maturity_level === 'ready' ? '#D1FAE5' : inv.self_assessment.maturity_level === 'mature' ? '#DBEAFE' : inv.self_assessment.maturity_level === 'developing' ? '#FEF3C7' : '#FEE2E2', color: inv.self_assessment.maturity_level === 'ready' ? '#065F46' : inv.self_assessment.maturity_level === 'mature' ? '#1E40AF' : inv.self_assessment.maturity_level === 'developing' ? '#92400E' : '#991B1B' }}>
                  {inv.self_assessment.maturity_level === 'ready' ? 'Ready' : inv.self_assessment.maturity_level === 'mature' ? 'Mature' : inv.self_assessment.maturity_level === 'developing' ? 'Developing' : 'Early Idea'}
                </Badge>
              )}
              {accessInfo && (
                <Badge className={`text-xs ${canEdit ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {canEdit ? 'Edit Access' : accessInfo.current_user_permission === 'feedback' ? 'Feedback Only' : 'View Only'}
                </Badge>
              )}
              {inv.social_actor_name && inv.social_actor_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/social-actor-registry?focus=${inv.social_actor_id}`)}
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  title="Open the owner organisation in the Social Actor Registry"
                  data-testid="detail-owner-org-badge"
                >
                  Owner: {inv.social_actor_name}
                </button>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-investment-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>

      {/* Pending changes banner */}
      {inv.pending_changes && (
        <Card className="border-amber-300 bg-amber-50" data-testid="pending-changes-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-900 font-semibold mb-1">
              <Clock className="h-5 w-5" /> Pending changes awaiting review
            </div>
            {(user?.role === 'administrator' || user?.role === 'project_manager') ? (
              <p className="text-sm text-amber-900">
                The owner submitted changes to this investment. Approve to publish them; the live record stays unchanged until you do.
              </p>
            ) : (
              <p className="text-sm text-amber-900">
                Your recent edits are pending review by an Administrator or Project Manager. The investment will not be updated until approved.
              </p>
            )}
            {inv.pending_changes_at && (
              <p className="text-xs text-amber-900 mt-1">
                Submitted: {new Date(inv.pending_changes_at).toLocaleString('en-GB')}
              </p>
            )}
            {(user?.role === 'administrator' || user?.role === 'project_manager') && (
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="approve-pending-changes-btn"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/social-investments/${inv.id}/pending-changes/approve`, {}, { headers });
                      toast.success('Pending changes approved.');
                      const refreshed = await axios.get(`${API}/api/social-investments/${inv.id}`, { headers });
                      setInv(refreshed.data);
                      if (onUpdated) onUpdated(refreshed.data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Approval failed');
                    }
                  }}
                >Approve changes</Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="reject-pending-changes-btn"
                  onClick={async () => {
                    const reason = window.prompt('Optional reason for rejecting these changes:') || '';
                    try {
                      await axios.post(`${API}/api/social-investments/${inv.id}/pending-changes/reject`, { reason }, { headers });
                      toast.success('Pending changes rejected.');
                      const refreshed = await axios.get(`${API}/api/social-investments/${inv.id}`, { headers });
                      setInv(refreshed.data);
                      if (onUpdated) onUpdated(refreshed.data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Reject failed');
                    }
                  }}
                >Reject</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Origin banner — shown when this investment was created from a Blended Plan */}
      {inv.bi_plan_id && (
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-sky-50" data-testid="bi-origin-banner">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-indigo-100 p-2 flex-shrink-0">
                <Link2 className="h-4 w-4 text-indigo-700" />
              </div>
              <div>
                <p className="font-semibold text-indigo-900">
                  Originates from Blended Plan{inv.bi_plan_title ? `: ${inv.bi_plan_title}` : ''}
                </p>
                <p className="text-sm text-slate-600 mt-0.5">
                  Once the plan reaches <span className="font-medium">Funded</span>, you can promote it back into the Projects Module from the plan page.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/combined-investments/${inv.bi_plan_id}`)}
              data-testid="goto-bi-plan-btn"
              className="whitespace-nowrap"
            >
              <ExternalLink className="h-3 w-3 mr-1" /> Open Plan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Seek Blended Funding CTA — when no BI plan link exists yet */}
      {canEdit && !inv.bi_plan_id && !inv.linked_bi_plan_id && (
        <Card className="border-purple-300 bg-purple-50/50" data-testid="seek-blended-cta">
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-purple-100 p-2 flex-shrink-0">
                <DollarSign className="h-4 w-4 text-purple-700" />
              </div>
              <div>
                <p className="font-semibold text-purple-900">Seek blended funding</p>
                <p className="text-sm text-slate-600 mt-0.5">
                  Promote this idea to the <span className="font-medium">Combined Investments</span> module
                  to attract a mix of public, philanthropic and private capital. Each funding source
                  becomes a bookable segment investors can commit to.
                </p>
              </div>
            </div>
            <Button
              onClick={convertToBlended}
              className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap"
              data-testid="seek-blended-btn"
            >
              <DollarSign className="h-4 w-4 mr-1" /> Seek Blended Funding
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Linked Blended Plan banner — once promoted */}
      {inv.linked_bi_plan_id && (
        <Card className="border-purple-200 bg-purple-50/50" data-testid="linked-bi-banner">
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div className="text-sm text-slate-700 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-purple-600" />
              <span>
                Promoted to Combined Investments on {inv.linked_bi_plan_at ? new Date(inv.linked_bi_plan_at).toLocaleDateString('en-GB') : '—'}
                {inv.linked_bi_plan_by ? ` by ${inv.linked_bi_plan_by}` : ''}.
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/combined-investments/${inv.linked_bi_plan_id}`)}
              data-testid="goto-linked-bi-btn"
            >
              <ExternalLink className="h-3 w-3 mr-1" /> Open Blended Plan
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="idea">
        <TabsList className="flex-wrap">
          <TabsTrigger value="idea" data-testid="tab-idea">Idea Summary</TabsTrigger>
          <TabsTrigger value="beneficiaries" data-testid="tab-beneficiaries">Beneficiaries & Impact</TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial">Financial Plan</TabsTrigger>
          <TabsTrigger value="stakeholders" data-testid="tab-stakeholders">Stakeholders</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Media & Files</TabsTrigger>
          <TabsTrigger value="assessment" data-testid="tab-assessment">Self-Assessment</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Link2 className="h-3.5 w-3.5 mr-1" /> Linked Modules
          </TabsTrigger>
          <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
          {canEdit && <TabsTrigger value="access" data-testid="tab-access">Access Rights</TabsTrigger>}
        </TabsList>

        {/* ====== TAB 1: IDEA SUMMARY ====== */}
        <TabsContent value="idea">
          <Card>
            <CardContent className="p-5 space-y-4">
              {!canEdit && (
                <div
                  className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center gap-2"
                  data-testid="detail-view-only-banner"
                >
                  <Eye className="h-3.5 w-3.5" />
                  <span>
                    You have view-only access. Only Administrators / Project Managers can edit; the social-actor that created this investment can submit changes for approval.
                  </span>
                </div>
              )}
              <fieldset disabled={!canEdit} className={!canEdit ? 'opacity-90 [&_input]:cursor-not-allowed [&_textarea]:cursor-not-allowed [&_button]:cursor-not-allowed' : ''}>
                <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={inv.title || ''} onChange={e => setInv({ ...inv, title: e.target.value })} data-testid="detail-title" readOnly={!canEdit} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={inv.description || ''} onChange={e => setInv({ ...inv, description: e.target.value })} rows={4} data-testid="detail-description" placeholder="Describe the social investment idea in detail..." readOnly={!canEdit} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Investment Type</Label>
                  <Select value={inv.investment_type || 'grant'} onValueChange={v => setInv({ ...inv, investment_type: v })} disabled={!canEdit}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Financing Instrument</Label>
                  <Select value={inv.financial_instrument || 'direct_grant'} onValueChange={v => setInv({ ...inv, financial_instrument: v })} disabled={!canEdit}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{INSTRUMENT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sector</Label>
                  <Input value={inv.sector || ''} onChange={e => setInv({ ...inv, sector: e.target.value })} placeholder="e.g., Digital Inclusion" readOnly={!canEdit} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Location</Label>
                  <Input value={inv.location || ''} onChange={e => setInv({ ...inv, location: e.target.value })} placeholder="City, Region, Country" readOnly={!canEdit} />
                </div>
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input value={(inv.tags || []).join(', ')} onChange={e => setInv({ ...inv, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })} placeholder="digital, elderly, rural" readOnly={!canEdit} />
                </div>
              </div>

              {/* Owner Social Actor */}
              <div>
                <Label>Owner organisation (Social Actor)</Label>
                <Select
                  value={inv.social_actor_id || 'none'}
                  onValueChange={v => setInv({ ...inv, social_actor_id: v === 'none' ? null : v, social_actor_name: v === 'none' ? null : (socialActors.find(a => a.id === v)?.organization_name || null) })}
                  disabled={!canEdit}
                >
                  <SelectTrigger data-testid="detail-social-actor-select"><SelectValue placeholder="Link this investment to a registered organisation" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Not linked —</SelectItem>
                    {socialActors.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.organization_name}{a.city ? ` · ${a.city}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {inv.social_actor_name && (
                  <p className="text-xs text-emerald-700 mt-1">Currently owned by: <strong>{inv.social_actor_name}</strong></p>
                )}
              </div>

              {/* SDG Alignment */}
              <div>
                <Label className="mb-2 block">SDG Alignment</Label>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(SDG_LABELS).map(([num, label]) => {
                    const n = parseInt(num);
                    const selected = (inv.sdg_alignment || []).includes(n);
                    return (
                      <button key={n} onClick={() => toggleSDG(n)}
                        className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
                        data-testid={`sdg-${n}`}>
                        {n}. {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Participatory Design Toggle */}
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-purple-50/30 border-purple-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inv.participatory_design_enabled || false}
                    onChange={e => setInv({ ...inv, participatory_design_enabled: e.target.checked })}
                    className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    data-testid="participatory-toggle"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable Participatory Design</span>
                </label>
                <span className="text-xs text-gray-500">Allow stakeholders to collaborate on planning through the Participatory Design module</span>
              </div>
                </div>
              </fieldset>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== TAB 2: BENEFICIARIES & IMPACT ====== */}
        <TabsContent value="beneficiaries">
          <Card>
            <CardContent className="p-5 space-y-5">
              <fieldset disabled={!canEdit} className={!canEdit ? '[&_input]:cursor-not-allowed [&_textarea]:cursor-not-allowed [&_button]:cursor-not-allowed' : ''}>
                <div className="space-y-5">
              {/* Target Groups */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold flex items-center gap-1.5"><Users className="h-4 w-4 text-blue-500" /> Target Groups</Label>
                  <Button size="sm" variant="outline" onClick={() => addToList('target_groups', { name: '', count: 0, description: '', is_vulnerable: false })} data-testid="add-target-group">
                    <Plus className="h-3 w-3 mr-1" /> Add Group
                  </Button>
                </div>
                {(inv.target_groups || []).length === 0 && <p className="text-xs text-gray-500 italic">No target groups defined</p>}
                {(inv.target_groups || []).map((tg, i) => (
                  <div key={i} className="border rounded-lg p-3 mb-2 bg-gray-50/50">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <Input value={tg.name || ''} onChange={e => updateListItem('target_groups', i, 'name', e.target.value)} placeholder="Group name" className="h-8 text-sm" />
                        <Input type="number" value={tg.count || ''} onChange={e => updateListItem('target_groups', i, 'count', parseInt(e.target.value) || 0)} placeholder="Count" className="h-8 text-sm" />
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                            <input type="checkbox" checked={tg.is_vulnerable || false} onChange={e => updateListItem('target_groups', i, 'is_vulnerable', e.target.checked)} className="rounded" />
                            Vulnerable
                          </label>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeFromList('target_groups', i)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                    <Input value={tg.description || ''} onChange={e => updateListItem('target_groups', i, 'description', e.target.value)} placeholder="Description..." className="mt-1 h-7 text-xs" />
                  </div>
                ))}
              </div>

              {/* Expected Outcomes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold flex items-center gap-1.5"><Target className="h-4 w-4 text-green-500" /> Expected Outcomes</Label>
                  <Button size="sm" variant="outline" onClick={() => addToList('expected_outcomes', { title: '', indicator: '', target: '' })} data-testid="add-outcome">
                    <Plus className="h-3 w-3 mr-1" /> Add Outcome
                  </Button>
                </div>
                {(inv.expected_outcomes || []).length === 0 && <p className="text-xs text-gray-500 italic">No outcomes defined</p>}
                {(inv.expected_outcomes || []).map((oc, i) => (
                  <div key={i} className="border rounded-lg p-3 mb-2 bg-gray-50/50">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <Input value={oc.title || ''} onChange={e => updateListItem('expected_outcomes', i, 'title', e.target.value)} placeholder="Outcome" className="h-8 text-sm" />
                        <Input value={oc.indicator || ''} onChange={e => updateListItem('expected_outcomes', i, 'indicator', e.target.value)} placeholder="Indicator" className="h-8 text-sm" />
                        <Input value={oc.target || ''} onChange={e => updateListItem('expected_outcomes', i, 'target', e.target.value)} placeholder="Target" className="h-8 text-sm" />
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeFromList('expected_outcomes', i)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
                </div>
              </fieldset>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== TAB 3: FINANCIAL PLAN ====== */}
        <TabsContent value="financial">
          <Card>
            <CardContent className="p-5 space-y-5">
              <fieldset disabled={!canEdit} className={!canEdit ? '[&_input]:cursor-not-allowed [&_button]:cursor-not-allowed' : ''}>
                <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Total Budget (EUR)</Label>
                  <Input type="number" value={inv.estimated_budget || ''} onChange={e => setInv({ ...inv, estimated_budget: parseFloat(e.target.value) || 0 })} data-testid="detail-budget" />
                </div>
                <div>
                  <Label>Timeline (months)</Label>
                  <Input type="number" value={inv.timeline_months || ''} onChange={e => setInv({ ...inv, timeline_months: parseInt(e.target.value) || 0 })} placeholder="18" />
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input type="date" value={inv.start_date || ''} onChange={e => setInv({ ...inv, start_date: e.target.value })} />
                </div>
              </div>

              {/* Budget Breakdown */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold flex items-center gap-1.5"><DollarSign className="h-4 w-4 text-emerald-700" /> Budget Breakdown</Label>
                  <Button size="sm" variant="outline" onClick={() => addToList('budget_breakdown', { category: '', amount: 0 })} data-testid="add-budget-item">
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                </div>
                {(inv.budget_breakdown || []).map((bb, i) => (
                  <div key={i} className="flex gap-2 mb-1.5">
                    <Input value={bb.category || ''} onChange={e => updateListItem('budget_breakdown', i, 'category', e.target.value)} placeholder="Category" className="flex-1 h-8 text-sm" />
                    <Input type="number" value={bb.amount || ''} onChange={e => updateListItem('budget_breakdown', i, 'amount', parseFloat(e.target.value) || 0)} placeholder="Amount" className="w-32 h-8 text-sm" />
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeFromList('budget_breakdown', i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
                {(inv.budget_breakdown || []).length > 0 && (
                  <div className="flex justify-end text-xs text-gray-500 mt-1 pr-10">
                    Total: EUR {(inv.budget_breakdown || []).reduce((s, b) => s + (b.amount || 0), 0).toLocaleString('el-GR')}
                  </div>
                )}
              </div>

              {/* Funding Sources */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-violet-500" /> Funding Sources</Label>
                  <Button size="sm" variant="outline" onClick={() => addToList('funding_sources', { source: '', amount: 0, type: 'grant' })} data-testid="add-funding-source">
                    <Plus className="h-3 w-3 mr-1" /> Add Source
                  </Button>
                </div>
                {(inv.funding_sources || []).map((fs, i) => (
                  <div key={i} className="flex gap-2 mb-1.5">
                    <Input value={fs.source || ''} onChange={e => updateListItem('funding_sources', i, 'source', e.target.value)} placeholder="Source name" className="flex-1 h-8 text-sm" />
                    <Input type="number" value={fs.amount || ''} onChange={e => updateListItem('funding_sources', i, 'amount', parseFloat(e.target.value) || 0)} placeholder="Amount" className="w-28 h-8 text-sm" />
                    <Select value={fs.type || 'grant'} onValueChange={v => updateListItem('funding_sources', i, 'type', v)} disabled={!canEdit}>
                      <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="grant">Grant</SelectItem>
                        <SelectItem value="equity">Equity</SelectItem>
                        <SelectItem value="loan">Loan</SelectItem>
                        <SelectItem value="donation">Donation</SelectItem>
                        <SelectItem value="in-kind">In-kind</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeFromList('funding_sources', i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
                </div>
              </fieldset>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== TAB 4: STAKEHOLDERS ====== */}
        <TabsContent value="stakeholders">
          <Card>
            <CardContent className="p-5">
              <fieldset disabled={!canEdit} className={!canEdit ? '[&_input]:cursor-not-allowed [&_button]:cursor-not-allowed' : ''}>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold flex items-center gap-1.5"><Globe className="h-4 w-4 text-indigo-500" /> Key Stakeholders & Partners</Label>
                <Button size="sm" variant="outline" onClick={() => addToList('stakeholders', { name: '', role: '', type: 'community' })} data-testid="add-stakeholder">
                  <Plus className="h-3 w-3 mr-1" /> Add Stakeholder
                </Button>
              </div>
              {(inv.stakeholders || []).length === 0 && <p className="text-xs text-gray-500 italic">No stakeholders defined</p>}
              {(inv.stakeholders || []).map((sh, i) => (
                <div key={i} className="border rounded-lg p-3 mb-2 bg-gray-50/50">
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input value={sh.name || ''} onChange={e => updateListItem('stakeholders', i, 'name', e.target.value)} placeholder="Name / Org" className="h-8 text-sm" />
                      <Input value={sh.role || ''} onChange={e => updateListItem('stakeholders', i, 'role', e.target.value)} placeholder="Role" className="h-8 text-sm" />
                      <Select value={sh.type || 'community'} onValueChange={v => updateListItem('stakeholders', i, 'type', v)} disabled={!canEdit}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">Public Sector</SelectItem>
                          <SelectItem value="private">Private Sector</SelectItem>
                          <SelectItem value="social_enterprise">Social Enterprise</SelectItem>
                          <SelectItem value="ngo">NGO / Civil Society</SelectItem>
                          <SelectItem value="community">Community</SelectItem>
                          <SelectItem value="academic">Academic / Research</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeFromList('stakeholders', i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
              </fieldset>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== TAB 5: MEDIA & FILES ====== */}
        <TabsContent value="media">
          <div className="space-y-4">
            <fieldset disabled={!canEdit} className={!canEdit ? '[&_input]:cursor-not-allowed [&_button]:cursor-not-allowed' : ''}>
            {/* Photos */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Image className="h-4 w-4 text-pink-500" /> Photos</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => photoInputRef.current?.click()} disabled={uploading} data-testid="upload-photo-btn">
                    <Upload className="h-3 w-3 mr-1" /> Upload Photo
                  </Button>
                  <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                </div>
              </CardHeader>
              <CardContent>
                {(inv.photos || []).length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-sm border-2 border-dashed rounded-lg">
                    <Image className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    No photos uploaded yet
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(inv.photos || []).map(photo => (
                      <div key={photo.id} className="relative group rounded-lg overflow-hidden border">
                        <img src={photo.data} alt={photo.filename} className="w-full h-32 object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button size="sm" variant="destructive" onClick={() => handleDeletePhoto(photo.id)} className="h-7 text-xs">
                            <Trash2 className="h-3 w-3 mr-1" /> Remove
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 px-2 py-1 truncate">{photo.filename}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Videos */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><Film className="h-4 w-4 text-red-600" /> Videos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <Input value={videoUrlInput} onChange={e => setVideoUrlInput(e.target.value)} placeholder="Paste YouTube or Vimeo URL..." className="flex-1 h-8 text-sm" data-testid="video-url-input" />
                  <Button size="sm" variant="outline" onClick={handleAddVideo} data-testid="add-video-btn"><Plus className="h-3 w-3 mr-1" /> Add</Button>
                </div>
                {(inv.video_urls || []).length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-4">No videos linked</p>
                ) : (
                  <div className="space-y-2">
                    {(inv.video_urls || []).map((url, i) => {
                      const ytId = getYouTubeId(url);
                      return (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg border bg-gray-50">
                          {ytId ? (
                            <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="thumbnail" className="w-24 h-14 object-cover rounded" />
                          ) : (
                            <div className="w-24 h-14 bg-gray-200 rounded flex items-center justify-center"><Film className="h-5 w-5 text-gray-500" /></div>
                          )}
                          <a href={url} target="_blank" rel="noopener noreferrer" className="flex-1 text-xs text-blue-600 hover:underline truncate">{url}</a>
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleRemoveVideo(url)}><X className="h-3 w-3" /></Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-1.5"><FileText className="h-4 w-4 text-blue-500" /> Documents & Files</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => attachInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
                    <Upload className="h-3 w-3 mr-1" /> Upload File
                  </Button>
                  <input ref={attachInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" onChange={handleAttachUpload} />
                </div>
              </CardHeader>
              <CardContent>
                {(inv.attachments || []).length === 0 ? (
                  <div className="text-center py-6 text-gray-500 text-sm border-2 border-dashed rounded-lg">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    No documents uploaded
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {(inv.attachments || []).map(att => (
                      <div key={att.id} className="flex items-center justify-between p-2 rounded-lg border bg-gray-50 hover:bg-gray-100">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">{att.filename}</p>
                            <p className="text-xs text-gray-500">{formatBytes(att.size)} · {att.uploaded_by} · {new Date(att.uploaded_at).toLocaleDateString('en-GB')}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {att.data && (
                            <a href={att.data} download={att.filename} className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded">Download</a>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteAttach(att.id)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            </fieldset>
          </div>
        </TabsContent>

        {/* ====== TAB 6: SELF-ASSESSMENT ====== */}
        <TabsContent value="assessment">
          {!canEdit ? (
            <Card><CardContent className="p-6 text-sm text-gray-500 flex items-center gap-2">
              <Eye className="h-4 w-4" /> Self-assessment can only be edited by Administrators / Project Managers or the social-actor that created this investment.
            </CardContent></Card>
          ) : (
            <InvestmentSelfAssessment
              investmentId={inv.id}
              existingAssessment={inv.self_assessment}
              onAssessmentSaved={(a) => setInv(prev => ({ ...prev, self_assessment: a }))}
            />
          )}
        </TabsContent>

        {/* ====== TAB 7: LINKED MODULES ====== */}
        <TabsContent value="integrations">
          <div className="space-y-4">
            {/* Capacity Building */}
            <Card className="border-blue-100">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Capacity Building for Social Actors</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Access training modules, courses, and resources to strengthen your investment proposal. Includes modules on impact measurement, financial planning, and participatory design.</p>
                      {inv.self_assessment && inv.self_assessment.total_score < 3.5 && (
                        <div className="mt-2 p-2 bg-amber-50 rounded-md border border-amber-100">
                          <p className="text-xs text-amber-700 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            <strong>Recommended:</strong> Your assessment score ({inv.self_assessment.total_score}/5) suggests training could help mature your idea.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => navigate('/capacity-building')} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="link-capacity-building">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Participatory Design */}
            <Card className={`${inv.participatory_design_enabled ? 'border-purple-200' : 'border-gray-100'}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${inv.participatory_design_enabled ? 'bg-purple-50' : 'bg-gray-50'}`}>
                      <UsersRound className={`h-5 w-5 ${inv.participatory_design_enabled ? 'text-purple-600' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Participatory Design</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Engage stakeholders and beneficiaries in co-designing the investment through participatory planning workshops, feedback collection, and collaborative decision-making.</p>
                      {inv.participatory_design_enabled ? (
                        <Badge className="mt-2 bg-purple-100 text-purple-700 text-xs">Enabled for this investment</Badge>
                      ) : (
                        <p className="mt-2 text-xs text-gray-500">Enable participatory design in the Idea Summary tab to activate this integration.</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={inv.participatory_design_enabled ? 'default' : 'outline'}
                    onClick={() => navigate('/participatory-planning')}
                    className={inv.participatory_design_enabled ? 'bg-purple-600 hover:bg-purple-700 shrink-0' : 'shrink-0'}
                    data-testid="link-participatory-design"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Investment Evaluation */}
            <Card className="border-amber-100">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <ClipboardCheck className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Investment Evaluation</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Evaluate this investment across 4 dimensions (Social Footprint, Extroversion, Innovation, Sustainability) with SROI calculation and Theory of Change mapping.</p>
                      {linkedEvaluations.length > 0 ? (
                        <div className="mt-2 space-y-1.5">
                          <p className="text-xs font-medium text-gray-700">{linkedEvaluations.length} evaluation(s) linked:</p>
                          {linkedEvaluations.map(ev => (
                            <div key={ev.id} className="flex items-center gap-2 p-1.5 rounded bg-gray-50 border">
                              <Badge className={`text-xs ${ev.stage === 'design' ? 'bg-blue-100 text-blue-700' : ev.stage === 'implementation' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                                {ev.stage}
                              </Badge>
                              <span className="text-xs text-gray-700 flex-1">{ev.investment_title}</span>
                              <span className="text-xs font-bold text-blue-700">{ev.weighted_total || '—'}/5</span>
                              <Badge variant="outline" className="text-xs capitalize">{ev.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-gray-500">No evaluations linked yet. Create one from the Evaluation module.</p>
                      )}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => navigate('/investment-evaluation')} className="bg-amber-700 hover:bg-amber-800 shrink-0" data-testid="link-evaluation">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Integration Summary */}
            <Card className="bg-gray-50/50">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 text-center">
                  These modules are part of the ISISP horizontal platform. Data from this investment can be used across evaluation, capacity building, and participatory planning workflows.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ====== TAB 8: FEEDBACK ====== */}
        <TabsContent value="feedback">
          <div className="space-y-4">
            {/* Submit Feedback */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Submit Feedback</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-2">
                  <Select value={feedbackType} onValueChange={setFeedbackType}>
                    <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recommendation">Recommendation</SelectItem>
                      <SelectItem value="comment">Comment</SelectItem>
                      <SelectItem value="concern">Concern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Textarea value={newFeedback} onChange={e => setNewFeedback(e.target.value)} rows={2} placeholder="Share your feedback, recommendations, or concerns about this investment..." className="text-sm" data-testid="feedback-input" />
                </div>
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={handleSubmitFeedback} disabled={!newFeedback.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-feedback-btn">Submit Feedback</Button>
                </div>
              </CardContent>
            </Card>

            {/* Feedback List */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">All Feedback ({feedbackList.length})</CardTitle></CardHeader>
              <CardContent>
                {feedbackList.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-6">No feedback yet. Be the first to share your thoughts.</p>
                ) : (
                  <div className="space-y-3">
                    {feedbackList.map(fb => (
                      <div key={fb.id} className="p-3 rounded-lg border bg-gray-50/50" data-testid={`feedback-${fb.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-800">{fb.username}</span>
                            <Badge variant="outline" className="text-xs capitalize">{fb.user_role?.replace('_', ' ')}</Badge>
                            <Badge className={`text-xs ${fb.feedback_type === 'recommendation' ? 'bg-blue-100 text-blue-700' : fb.feedback_type === 'concern' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                              {fb.feedback_type}
                            </Badge>
                          </div>
                          {(user?.role === 'administrator' || user?.role === 'project_manager' || user?.id === fb.user_id || user?.id === inv.creator_id) && (
                            <Button size="sm" variant="ghost" className="text-red-400 h-6" onClick={() => handleDeleteFeedback(fb.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{fb.content}</p>
                        <p className="text-xs text-gray-500 mt-1">{new Date(fb.created_at).toLocaleString('en-GB')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ====== TAB 9: ACCESS RIGHTS ====== */}
        {canEdit && (
          <TabsContent value="access">
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Access Rights</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500 mb-4">
                    The creator, administrators, and project managers always have edit access. You can grant additional view, edit, or feedback access to other users.
                  </p>

                  {/* Grant Access */}
                  <div className="border rounded-lg p-3 bg-gray-50 mb-4">
                    <Label className="text-xs font-medium mb-2 block">Grant Access to User</Label>
                    <div className="flex gap-2">
                      <Input value={grantUserId} onChange={e => setGrantUserId(e.target.value)} onFocus={fetchAllUsers} placeholder="User ID" className="flex-1 h-8 text-xs" list="user-suggestions" data-testid="grant-user-input" />
                      <datalist id="user-suggestions">
                        {allUsers.map(u => <option key={u.id} value={u.id}>{u.username} ({u.role})</option>)}
                      </datalist>
                      <Select value={grantPermission} onValueChange={setGrantPermission}>
                        <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">View</SelectItem>
                          <SelectItem value="edit">Edit</SelectItem>
                          <SelectItem value="feedback">Feedback</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={handleGrantAccess} className="bg-blue-600 hover:bg-blue-700 h-8" data-testid="grant-access-btn">Grant</Button>
                    </div>
                    {allUsers.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {allUsers.slice(0, 8).map(u => (
                          <button key={u.id} onClick={() => setGrantUserId(u.id)} className={`text-xs px-2 py-0.5 rounded-full border ${grantUserId === u.id ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-200'}`}>
                            {u.username} <span className="text-gray-500">({u.role?.replace('_', ' ')})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Current Grants */}
                  <div>
                    <Label className="text-xs font-medium mb-2 block">Current Access Grants</Label>
                    {/* Built-in access */}
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-center justify-between p-2 rounded-lg bg-green-50/50 border border-green-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-700">{inv.creator_username || 'Creator'}</span>
                          <Badge className="text-xs bg-green-100 text-green-700">Creator — Edit</Badge>
                        </div>
                        <span className="text-xs text-gray-500">Built-in</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded-lg bg-blue-50/50 border border-blue-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-700">All Administrators & PMs</span>
                          <Badge className="text-xs bg-blue-100 text-blue-700">Edit</Badge>
                        </div>
                        <span className="text-xs text-gray-500">Built-in</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-700">Other Social Actors & Guests</span>
                          <Badge className="text-xs bg-gray-100 text-gray-600">Feedback</Badge>
                        </div>
                        <span className="text-xs text-gray-500">Default</span>
                      </div>
                    </div>

                    {/* Explicit grants */}
                    {(accessInfo?.grants || []).length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs text-gray-500 font-medium">Explicit Grants:</p>
                        {(accessInfo?.grants || []).map(g => (
                          <div key={g.user_id} className="flex items-center justify-between p-2 rounded-lg border bg-white" data-testid={`access-grant-${g.user_id}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-700">{g.username}</span>
                              <Badge className={`text-xs ${g.permission === 'edit' ? 'bg-green-100 text-green-700' : g.permission === 'feedback' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{g.permission}</Badge>
                              <span className="text-xs text-gray-500">by {g.granted_by}</span>
                            </div>
                            <Button size="sm" variant="ghost" className="text-red-400 h-6" onClick={() => handleRevokeAccess(g.user_id)} data-testid={`revoke-${g.user_id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default InvestmentDetailView;
